/**
 * notes-storage.js
 * Pure Dexie (IndexedDB) interface for the local notes store.
 * No business logic — all operations are synchronous from the caller's perspective.
 */

import { db } from './db';

export const notesStorage = {
  /**
   * All notes for a district, including dirty and pending-delete ones.
   * Caller is responsible for filtering _deleted flags.
   * @param {string} districtId
   * @returns {Promise<Object[]>}
   */
  async getAll(districtId) {
    return db.notes.where('district_id').equals(districtId).toArray();
  },

  /**
   * @param {string} id
   * @returns {Promise<Object|undefined>}
   */
  async getById(id) {
    return db.notes.get(id);
  },

  /**
   * Upsert a note (create or replace).
   * @param {Object} note
   * @returns {Promise<Object>} the same note
   */
  async save(note) {
    await db.notes.put(note);
    return note;
  },

  /**
   * Hard-delete from local store.
   * @param {string} id
   */
  async delete(id) {
    await db.notes.delete(id);
  },

  /**
   * Notes with unsent local changes (dirty flag).
   * Includes pending-delete notes that have not yet reached Supabase.
   * @param {string} districtId
   * @returns {Promise<Object[]>}
   */
  async getDirty(districtId) {
    const all = await db.notes
      .where('district_id')
      .equals(districtId)
      .toArray();
    return all.filter((n) => n.dirty === true);
  },

  /**
   * Clear dirty flag after a successful sync.
   * @param {string} id
   */
  async markSynced(id) {
    await db.notes.update(id, {
      dirty: false,
      _local_only: false,
      sync_status: 'synced',
    });
  },

  /**
   * Mark a sync attempt as failed (retryable).
   * @param {string} id
   */
  async markFailed(id) {
    await db.notes.update(id, { sync_status: 'failed' });
  },

  /**
   * Notes linked to a specific entity.
   * Uses `linked_id` index + filter for type/district safety.
   * @param {string} districtId
   * @param {string} linkedType — 'person' | 'family' | 'church' | 'event' | 'preaching' | 'visit'
   * @param {string} linkedId
   * @returns {Promise<Object[]>} sorted newest first
   */
  async getByEntity(districtId, linkedType, linkedId) {
    const all = await db.notes
      .where('linked_id')
      .equals(linkedId)
      .toArray();
    return all
      .filter(
        (n) =>
          n.district_id === districtId &&
          n.linked_type === linkedType &&
          !n._deleted
      )
      .sort((a, b) => {
        const ta = a.updated_locally_at || a.updated_at || a.created_at || '';
        const tb = b.updated_locally_at || b.updated_at || b.created_at || '';
        return tb.localeCompare(ta);
      });
  },

  /**
   * Stamp last_viewed_at without triggering a sync (local metadata only).
   * @param {string} id
   */
  async touchViewed(id) {
    await db.notes.update(id, { last_viewed_at: new Date().toISOString() });
  },
};
